import { Pill } from '@/components/UI/Pill'
import { PillSelect } from '@/components/UI/PillSelect'
import type { PillSelectOption } from '@/components/UI/PillSelect'
import type { SpotTagData } from '@/schemas/v2/spot'
import styles from './SpotFilterRow.module.css'

type SpotFilterRowProps = {
  /** PillSelect 用シートオプション（先頭に「すべてのコース」（value = ''）を含む） */
  sheetOptions: PillSelectOption[]
  /** 現在選択中のシート値（空文字 = すべて） */
  selectedSheet: string
  /** シート変更コールバック */
  onSheetChange: (value: string) => void
  /** タグ一覧 */
  tags: SpotTagData[]
  /** 現在選択中のタグID（null = 未選択） */
  selectedTagId: number | null
  /** タグクリックコールバック */
  onTagClick: (tagId: number) => void
  /** コンテナに追加する className（余白のカスタマイズ用） */
  className?: string
}

/** 「すべてのコース」を表す option の value（selectedSheet の「すべて」と同値） */
const ALL_SHEETS_VALUE = ''

/** フィルター行のレイアウト（SpotFilterRowSkeleton と共有）。高さは Pill 24px + py-2×2 = 40px */
export const SPOT_FILTER_ROW_CLASS = 'flex items-center gap-2 px-4 py-2'

/**
 * スポット一覧ページのフィルター行コンポーネント。
 * PillSelect（シート選択）と Pill ボタン（タグ絞り込み）を横スクロール行に並べる。
 *
 * - PillSelect は実シート（「すべてのコース」を除く）が 2 件以上のときのみ表示する（#3074）
 *   シートが 1 件だと「すべてのコース」と実質同じ絞り込みにしかならないため
 * - Pill ボタンはタグが 1 件以上のときのみ表示する
 * - 両方表示しない場合は null を返す
 */
export function SpotFilterRow({
  sheetOptions,
  selectedSheet,
  onSheetChange,
  tags,
  selectedTagId,
  onTagClick,
  className,
}: SpotFilterRowProps) {
  const sheetCount = sheetOptions.filter((option) => option.value !== ALL_SHEETS_VALUE).length
  const showPillSelect = sheetCount >= 2
  const showTags = tags.length > 0

  if (!showPillSelect && !showTags) return null

  return (
    <div
      data-testid="spot-filter-row"
      className={`${SPOT_FILTER_ROW_CLASS} overflow-x-auto ${className ?? ''} ${styles.filterRow}`}
    >
      {showPillSelect && <PillSelect options={sheetOptions} value={selectedSheet} onChange={onSheetChange} />}
      {showTags &&
        tags.map((tag) => {
          const isSelected = selectedTagId === tag.id

          return (
            <button
              key={tag.id}
              type="button"
              className="shrink-0"
              aria-pressed={isSelected}
              aria-label={tag.name}
              onClick={() => onTagClick(tag.id)}
            >
              <Pill type={isSelected ? 'Active' : 'Inactive'} label={tag.name} />
            </button>
          )
        })}
    </div>
  )
}
